import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import {
  Users,
  Plus,
  Search,
  Shield,
  Trash2,
  Edit2,
  Loader2,
  UserCheck,
  UserX,
  Key,
  Mail,
  Phone,
} from "lucide-react";
import { formatDate } from "@ckgo2/shared/utils";
import toast from "react-hot-toast";

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  roleLabel: string;
  phone?: string;
  avatar?: string;
  isActive: boolean;
  lastLoginAt?: string;
  createdAt: string;
}

interface Role {
  value: string;
  label: string;
  description: string;
}

const ROLE_COLORS: Record<string, string> = {
  admin: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  editor: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  marketing: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  sales: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  readonly: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
};

export default function UsersPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null);

  // Fetch users
  const { data: usersData, isLoading, error: usersError } = useQuery({
    queryKey: ["users"],
    queryFn: () => api.get<ApiResponse<User[]>>("/api/admin/users"),
  });

  // Fetch roles
  const { data: rolesData } = useQuery({
    queryKey: ["roles"],
    queryFn: () => api.get<ApiResponse<Role[]>>("/api/admin/users/roles"),
  });

  const users = usersData?.data || [];
  // Fallback roles in case API hasn't loaded yet
  const roles = rolesData?.data || [
    { value: "admin", label: "Administrátor", description: "Plný přístup ke všem funkcím" },
    { value: "editor", label: "Editor obsahu", description: "Správa zájezdů, destinací, průvodců, recenzí a médií" },
    { value: "marketing", label: "Marketing", description: "Newsletter, segmenty, automatické kampaně, zákazníci" },
    { value: "sales", label: "Obchod", description: "Objednávky, zákazníci, poptávky" },
    { value: "readonly", label: "Pouze prohlížení", description: "Pouze prohlížení bez možnosti úprav" },
  ];

  // Filter users
  const filteredUsers = users.filter(
    (u) =>
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase()) ||
      u.roleLabel.toLowerCase().includes(search.toLowerCase())
  );

  // Toggle active mutation
  const toggleMutation = useMutation({
    mutationFn: (id: string) =>
      api.patch<ApiResponse<{ isActive: boolean }>>(`/api/admin/users/${id}/toggle`),
    onSuccess: () => {
      toast.success("Stav uživatele změněn");
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Nepodařilo se změnit stav uživatele");
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/users/${id}`),
    onSuccess: () => {
      toast.success("Uživatel smazán");
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Nepodařilo se smazat uživatele");
    },
  });

  const handleDelete = (user: User) => {
    if (window.confirm(`Opravdu chcete smazat uživatele ${user.name}?`)) {
      deleteMutation.mutate(user.id);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Správa uživatelů
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Spravujte přístupy do administrace
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary"
        >
          <Plus className="h-5 w-5" />
          Nový uživatel
        </button>
      </div>

      {/* Role legend */}
      <div className="flex flex-wrap gap-3">
        {roles.map((role) => (
          <div
            key={role.value}
            className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800"
            title={role.description}
          >
            <span className={`badge ${ROLE_COLORS[role.value]}`}>
              {role.label}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {role.description}
            </span>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Hledat uživatele..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input pl-10"
        />
      </div>

      {/* Users table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
          </div>
        ) : usersError ? (
          <div className="flex h-32 flex-col items-center justify-center text-error-500">
            <p className="font-medium">Chyba při načítání uživatelů</p>
            <p className="text-sm text-gray-500">{(usersError as Error).message}</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="flex h-32 flex-col items-center justify-center text-gray-500">
            <Users className="mb-2 h-8 w-8 text-gray-300" />
            <p>Žádní uživatelé</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Uživatel
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Role
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Stav
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Poslední přihlášení
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                    Akce
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {user.name}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {user.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`badge ${ROLE_COLORS[user.role]}`}>
                        {user.roleLabel}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${
                          user.isActive
                            ? "bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400"
                            : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                        }`}
                      >
                        {user.isActive ? (
                          <>
                            <UserCheck className="h-3 w-3" />
                            Aktivní
                          </>
                        ) : (
                          <>
                            <UserX className="h-3 w-3" />
                            Neaktivní
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {user.lastLoginAt ? formatDate(user.lastLoginAt) : "Nikdy"}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setResetPasswordUser(user)}
                          className="btn-ghost btn-sm"
                          title="Resetovat heslo"
                        >
                          <Key className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => toggleMutation.mutate(user.id)}
                          disabled={toggleMutation.isPending}
                          className="btn-ghost btn-sm"
                          title={user.isActive ? "Deaktivovat" : "Aktivovat"}
                        >
                          {user.isActive ? (
                            <UserX className="h-4 w-4" />
                          ) : (
                            <UserCheck className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={() => setEditingUser(user)}
                          className="btn-ghost btn-sm"
                          title="Upravit"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(user)}
                          className="btn-ghost btn-sm text-error-600 hover:bg-error-50"
                          title="Smazat"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <UserFormModal
          roles={roles}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {/* Edit Modal */}
      {editingUser && (
        <UserFormModal
          user={editingUser}
          roles={roles}
          onClose={() => setEditingUser(null)}
        />
      )}

      {/* Reset Password Modal */}
      {resetPasswordUser && (
        <ResetPasswordModal
          user={resetPasswordUser}
          onClose={() => setResetPasswordUser(null)}
        />
      )}
    </div>
  );
}

// User Form Modal (Create/Edit)
function UserFormModal({
  user,
  roles,
  onClose,
}: {
  user?: User;
  roles: Role[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const isEdit = !!user;

  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [role, setRole] = useState(user?.role || "readonly");
  const [password, setPassword] = useState("");
  const [isActive, setIsActive] = useState(user?.isActive ?? true);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: {
      name: string;
      email: string;
      password: string;
      role: string;
      phone?: string;
      isActive: boolean;
    }) => api.post<ApiResponse<User>>("/api/admin/users", data),
    onSuccess: () => {
      toast.success("Uživatel vytvořen");
      queryClient.invalidateQueries({ queryKey: ["users"] });
      onClose();
    },
    onError: (error: Error) => {
      toast.error(error.message || "Nepodařilo se vytvořit uživatele");
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: {
      name?: string;
      email?: string;
      role?: string;
      phone?: string;
      password?: string;
      isActive?: boolean;
    }) => api.put<ApiResponse<User>>(`/api/admin/users/${user?.id}`, data),
    onSuccess: () => {
      toast.success("Uživatel aktualizován");
      queryClient.invalidateQueries({ queryKey: ["users"] });
      onClose();
    },
    onError: (error: Error) => {
      toast.error(error.message || "Nepodařilo se aktualizovat uživatele");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !email.trim()) {
      toast.error("Vyplňte jméno a email");
      return;
    }

    if (!isEdit && (!password || password.length < 8)) {
      toast.error("Heslo musí mít alespoň 8 znaků");
      return;
    }

    if (isEdit) {
      updateMutation.mutate({
        name,
        email,
        role,
        phone: phone || undefined,
        password: password || undefined,
        isActive,
      });
    } else {
      createMutation.mutate({
        name,
        email,
        password,
        role,
        phone: phone || undefined,
        isActive,
      });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 mx-4 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-2xl dark:bg-gray-800">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          {isEdit ? "Upravit uživatele" : "Nový uživatel"}
        </h2>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div>
            <label className="label">Jméno *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jan Novák"
              className="input"
              required
            />
          </div>

          <div>
            <label className="label">Email *</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jan@firma.cz"
                className="input pl-10"
                required
              />
            </div>
          </div>

          <div>
            <label className="label">Telefon</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+420 123 456 789"
                className="input pl-10"
              />
            </div>
          </div>

          <div>
            <label className="label">Role *</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="input"
              required
            >
              {roles.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label} - {r.description}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">
              Heslo {isEdit ? "(ponechte prázdné pro zachování)" : "*"}
            </label>
            <div className="relative">
              <Key className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isEdit ? "••••••••" : "Min. 8 znaků"}
                className="input pl-10"
                required={!isEdit}
                minLength={isEdit ? 0 : 8}
              />
            </div>
          </div>

          <div className="flex items-center gap-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
            <input
              type="checkbox"
              id="isActive"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4 rounded text-primary-600"
            />
            <label htmlFor="isActive" className="text-gray-700 dark:text-gray-300">
              Účet je aktivní
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <button type="button" onClick={onClose} className="btn-ghost">
              Zrušit
            </button>
            <button type="submit" disabled={isPending} className="btn-primary">
              {isPending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : isEdit ? (
                "Uložit"
              ) : (
                "Vytvořit"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Reset Password Modal
function ResetPasswordModal({
  user,
  onClose,
}: {
  user: User;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [password, setPassword] = useState("");

  const resetMutation = useMutation({
    mutationFn: (newPassword: string) =>
      api.post(`/api/admin/users/${user.id}/reset-password`, { password: newPassword }),
    onSuccess: () => {
      toast.success("Heslo bylo resetováno");
      queryClient.invalidateQueries({ queryKey: ["users"] });
      onClose();
    },
    onError: (error: Error) => {
      toast.error(error.message || "Nepodařilo se resetovat heslo");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Heslo musí mít alespoň 8 znaků");
      return;
    }
    resetMutation.mutate(password);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 mx-4 w-full max-w-md rounded-xl bg-white p-6 shadow-2xl dark:bg-gray-800">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          Resetovat heslo
        </h2>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Nastavit nové heslo pro uživatele <strong>{user.name}</strong>
        </p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div>
            <label className="label">Nové heslo *</label>
            <div className="relative">
              <Key className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min. 8 znaků"
                className="input pl-10"
                required
                minLength={8}
                autoFocus
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <button type="button" onClick={onClose} className="btn-ghost">
              Zrušit
            </button>
            <button
              type="submit"
              disabled={resetMutation.isPending}
              className="btn-primary"
            >
              {resetMutation.isPending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                "Resetovat heslo"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
